<script setup>
import { ref, onMounted } from 'vue';
import KnowledgeBaseAPI from 'dashboard/api/knowledgeBase';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const isLoading = ref(true);
const isSaving = ref(false);

const persona = ref({
  persona_name: '',
  persona_tone: '',
  greeting: '',
});

const status = ref({
  wecom: {},
  today: {},
});

const loadData = async () => {
  isLoading.value = true;
  try {
    const [pRes, sRes] = await Promise.all([
      KnowledgeBaseAPI.getPersona(),
      KnowledgeBaseAPI.getDigitalWorkerStatus(),
    ]);
    persona.value = {
      persona_name: pRes.data?.persona?.persona_name || '',
      persona_tone: pRes.data?.persona?.persona_tone || '',
      greeting: pRes.data?.persona?.greeting || '',
    };
    status.value = sRes.data || {};
  } catch (error) {
    useAlert(error.message || '加载数字人配置失败');
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    await KnowledgeBaseAPI.updatePersona(persona.value);
    useAlert('数字人配置已保存，下一条 AI 回复即时生效 ✓');
  } catch (error) {
    useAlert(error.response?.data?.error || error.message || '保存失败');
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div class="flex flex-col gap-6 max-w-3xl">
    <!-- 渠道状态指示卡片 -->
    <div class="p-4 border rounded-xl bg-n-solid-2 border-n-weak flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div class="flex items-center gap-3">
        <div class="p-2.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
          <svg class="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"/>
            <path d="M12 6v6l4 2"/>
          </svg>
        </div>
        <div>
          <div class="flex items-center gap-2">
            <span class="text-sm font-semibold text-n-slate-12">微信客服渠道连接</span>
            <span
              class="px-2 py-0.5 text-xs font-semibold rounded-full"
              :class="status.wecom?.ok ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' : 'bg-n-alpha-2 text-n-slate-11 border border-n-weak'"
            >
              {{ status.wecom?.ok ? '已配置就绪' : '待配置' }}
            </span>
          </div>
          <p class="text-xs text-n-slate-10 mt-0.5">
            今日 AI 回复数：{{ status.today?.bot_reply || 0 }} · 转人工数：{{ status.today?.escalation || 0 }}
          </p>
        </div>
      </div>
    </div>

    <!-- 表单卡片 -->
    <div class="p-6 border rounded-xl bg-n-solid-2 border-n-weak flex flex-col gap-5">
      <div class="border-b pb-4 border-n-weak">
        <h3 class="text-base font-semibold text-n-slate-12">数字人人设与对话语气</h3>
        <p class="text-xs text-n-slate-10 mt-1">
          配置 AI 数字人客服的对外称呼、沟通语气以及访客进入时的自动首句问候语。
        </p>
      </div>

      <!-- 数字人名称 -->
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-n-slate-11">数字人称呼 / 名字</label>
        <input
          v-model="persona.persona_name"
          type="text"
          placeholder="例如：小艾、安安、商城客服小助手（留空则默认：电商客服坐席）"
          class="reset-base !mb-0 w-full px-3.5 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10 focus:bg-n-alpha-2 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
        />
      </div>

      <!-- 语气风格 -->
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-n-slate-11">对话风格与语气</label>
        <input
          v-model="persona.persona_tone"
          type="text"
          placeholder="例如：亲切自然、热情专业、简明高效、礼貌得体"
          class="reset-base !mb-0 w-full px-3.5 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10 focus:bg-n-alpha-2 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
        />
      </div>

      <!-- 自动问候语 -->
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-n-slate-11">入站自动首句问候语</label>
        <textarea
          v-model="persona.greeting"
          rows="3"
          placeholder="例如：您好！我是智能客服小艾，请问有什么可以帮您？（访客从微信客服或网页首次接入时自动下发）"
          class="reset-base !mb-0 w-full px-3.5 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10 focus:bg-n-alpha-2 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
        />
      </div>

      <!-- 保存操作 -->
      <div class="flex justify-end pt-2">
        <Button
          :label="isSaving ? '保存中…' : '保存设置'"
          :is-loading="isSaving"
          :disabled="isSaving"
          color="blue"
          size="sm"
          @click="save"
        />
      </div>
    </div>
  </div>
</template>
